<script lang="ts" setup>
import type { AsyncDataRequestStatus, NuxtError } from '#app'
import { useModal } from 'vue-final-modal'
import type { Head } from 'zhead'

import DefaultBoxSection from "~/components/DefaultBoxSection.vue";
import ToggleRow from '~/components/form-elements/ToggleRow.vue'
import ToggleRowItem from '~/components/form-elements/ToggleRowItem.vue'
import AddProductToList from "~/components/modal-windows/AddProductToList.vue";
import CreateSubscriber from '~/components/modal-windows/CreateSubscriber.vue'
import ShareLink from '~/components/modal-windows/ShareLink.vue'
import Disclaimer from '~/components/products/Disclaimer.vue'
import ProductAside from '~/components/products/ProductAside.vue'
import ProductAttributes from '~/components/products/ProductAttributes.vue'
import ProductBrand from "~/components/products/ProductBrand.vue";
import ProductCategories from "~/components/products/ProductCategories.vue";
import ProductDescription from '~/components/products/ProductDescription.vue'
import ProductFacts from "~/components/products/ProductFacts.vue";
import ProductFooterSticky from '~/components/products/ProductFooterSticky.vue'
import ProductHeader from '~/components/products/ProductHeader.vue'
import ProductLink from '~/components/products/ProductLink.vue'
import ProductResearches from '~/components/products/ProductResearches.vue'
import ProductReviews from '~/components/products/ProductReviews.vue'
import ReviewsSidebar from '~/components/products/ReviewsSidebar.vue'
import ConsultationsSection from '~/components/sections/ConsultationsSection.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import ProductQuestionsSection from '~/components/sections/ProductQuestionsSection.vue'
import ProductQuestionsSectionSidebar from '~/components/sections/ProductQuestionsSectionSidebar.vue'
import SidebarWithMainSection from '~/components/sections/SidebarWithMainSection.vue'
import { usePopups } from '~/composables/popups'
import { useSharing } from '~/composables/sharing'
import constants from '~/constants'
import ImageType from '~/enums/image-type'
import { MindboxOperation } from '~/enums/mindbox-operation'
import SubscriptionForm from '~/enums/subscription-form'
import YandexMetrikaGoal from '~/enums/yandex-metrika-goal'
import helpers from '~/helpers'
import JsonApiHelper from '~/helpers/json-api-helper'
import EcommerceApiService from '~/services/ecommerce-api-service'
import { viewProduct } from '~/services/mindbox-api-service'
import YandexMetrikaService from '~/services/yandex-metrika-service'
import { useAiReviewsStore } from '~/store/ai-reviews'
import { useAuthStore } from '~/store/auth'
import { useProductQuestionsStore } from '~/store/product-questions'
import type { BreadcrumbsItem } from '~/types/breadcrumbs'
import type { CategoryData } from '~/types/categories'
import type { ProductData } from '~/types/products'

const enum Tabs {
  Reviews,
  Questions
}

const previewTypes = [ImageType.Thumbnail240p, ImageType.Original]

const { $apiHelper } = useNuxtApp()
const route = useRoute()
const router = useRouter()

const aiReviewsStore = useAiReviewsStore()
const authStore = useAuthStore()
const productQuestionsStore = useProductQuestionsStore()

const { parsePromoCodeFromQuery } = useSharing()

const activeTab = ref<Tabs>(Tabs.Reviews)
const actualFullPath = ref<string | null>(null)

// const activeIngredientsIds = computed(() => productData.value!.meta.activeIngredientsIds ?? [])
// const bodySystemsIds = computed(() => productData.value!.meta.bodySystemsIds ?? [])
const brandData = computed(() => productData.value!.relationships.brand?.data)
const categoriesData = computed(() => productData.value!.relationships.categories?.data ?? [])
const getBreadcrumbItems = computed<BreadcrumbsItem[]>(() => [
  { name: 'Каталог', path: '/search' },
  { name: seoH1.value, path: helpers.getProductPath(productData.value!) }
])
const id = computed(() => productData.value!.id)
const isConsultation = computed(
  () => productData.value!.relationships.categories.data.some(
    (x: CategoryData): boolean => x.id === constants.categories.consultations.id
  )
)
const isMarathon = computed(() => [3936, 3937].includes(Number(id.value)))
const isPurchasedByUser = computed(() => productData.value!.meta.isPurchasedByUser)
const multiplicity = computed(() => productData.value!.attributes.multiplicity ?? 1)
const name = computed(() => productData.value!.attributes.name)
const price = computed(() => helpers.getProductPrice(productData.value!))
const productId = computed(() => helpers.getIdFromSlugAndId(route.params.slugAndId as string))
const productKey = computed(() => `product-${productId.value}`)
const seoData = computed(() => productData.value!.attributes.seoData)
const seoH1 = computed(() => seoData.value?.h1 ?? name.value)
const rating = computed(() => {
  const { rating } = productData.value!.attributes

  return typeof rating === 'number' ? rating : null
})
const totalPublishedReviews = computed(() => productData.value!.attributes.totalPublishedReviews)
const userReviewId = computed(() => productData.value!.meta.userReviewId)

const { data, error, status } = await useLazyAsyncData(
  productKey,
  async () => {
    try {
      const response = await $apiHelper.products.getProduct(productId.value!)

      const { fullPath, path } = route
      const actualPath = helpers.getProductPath2(response.data, path)

      if (path !== actualPath) {
        actualFullPath.value = fullPath.replace(path, actualPath)

        if (import.meta.client) {
          history.replaceState({}, '', actualFullPath.value)
        }
      }

      return response
    } catch (error) {
      showError(error as NuxtError)
    }
  },
  {
    transform: x => JsonApiHelper.denormalizeResponse(x)
  }
)

if (error.value) {
  throw createError(error.value as NuxtError)
}

if (actualFullPath.value) {
  await navigateTo(actualFullPath.value, { redirectCode: 301 })
}

const productData = computed<ProductData | null>(() => data.value?.data ?? null)

watch(status, async (x: AsyncDataRequestStatus): Promise<void> => {
  if (x === 'success') { // After preloading.
    if (totalPublishedReviews.value > 0) {
      await aiReviewsStore.fetchAiDetails(id.value)
    }

    await trackPageView()
  }
})

useHead(() => {
  if (status.value !== 'success') {
    return {}
  }

  const previewUrl = getPreviewUrl()

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productData.value!.attributes.name,
    offers: { '@type': 'Offer', price: price.value, priceCurrency: 'RUB' }
  }

  if (brandData.value) {
    (jsonLd as any).brand = { '@type': 'Brand', name: brandData.value.attributes.name }
  }

  if (productData.value!.attributes.descriptionHtml) {
    (jsonLd as any).description = productData.value!.attributes.descriptionHtml
  }

  if (previewUrl) {
    (jsonLd as any).image = previewUrl
  }

  const head: Head = {
    title: seoData.value?.title ?? `Купить ${seoH1.value} по цене ${price.value} ₽ в интернет-магазине iTAB | наличие, стоимость, доставка, отзывы, заказ на сайте`,
    meta: [
      {
        name: 'description',
        content: seoData.value?.description
          ?? `Купить ${seoH1.value} в Москве и с доставкой во все регионы России и по миру. Ознакомиться с каталогом и ценами и заказать онлайн в интернет-магазине iTAB. Описание продукта, состав, инструкция по применению, отзывы покупателей. Широкий ассортимент, известные производители, скидки и акции.`
      },
      { property: 'og:description', content: '' },
      { name: 'og:title', property: 'og:title', content: `${seoH1.value} — iTAB` }
    ],
    script: [
      {
        innerHTML: JSON.stringify(jsonLd),
        type: 'application/ld+json'
      }
    ]
  }

  if (head.meta && previewUrl) {
    head.meta.push(...helpers.getPreviewMetadata(previewUrl))
  }

  return head
})

onMounted(async (): Promise<void> => {
  YandexMetrikaService.sendMetricEvent(YandexMetrikaGoal.ViewProduct)

  await parsePromoCodeFromQuery()

  const query = { ...route.query }

  if (productId.value === '2659') {
    const { showPopupIfNeeded } = usePopups()

    showPopupIfNeeded({
      id: '4',
      type: 'popups',
      attributes: {
        action: null,
        actionParameters: {},
        buttonText: 'Участвовать',
        delayAfterClosing: 60,
        delayBeforeShowing: 10,
        descriptionHtml: '<p>Ты часто устаёшь? Кружится голова? Возможно, у тебя дефицит железа!</p>'
          + '<p>Biomins приглашает тебя в мониторинг здоровья с нашим Железом бисглицинат хелатом + B9 и B12 — и дарит продукт участникам!</p>'
          + '<p>📊 Что тебя ждёт:<br>'
          + '✔ Контроль анализов (гемоглобин, ферритин, гомоцистеин) и скидки на прохождение тестов в лабораториях.<br>'
          + '✔ Эффективная добавка — без побочек и с высоким усвоением.<br>'
          + '✔ Результаты уже через 1,5 месяца (по опыту прошлых участников!).<br>'
          + '✔ Подарок — получи продукт бесплатно после мониторинга.</p>'
          + '<p>🚀 <strong>Успей занять место! Количество участников ограничено.</strong></p>',
        isActive: true,
        name: '🔥 Повысьте гемоглобин и получи железо в подарок! 🔥',
        path: null,
        url: 'https://docs.google.com/forms/d/e/1FAIpQLSeZpByDq5J0VZZOoOZmHlsCCLC1pWC5q1QpHcLKq25iORZR8g/viewform'
      },
      meta: {
        imageUrl: 'https://cdn.itab.pro/popups/4.jpg'
      }
    })
  }

  if (status.value === 'success') { // After SSR.
    if (totalPublishedReviews.value > 0) {
      await aiReviewsStore.fetchAiDetails(id.value)
    }

    const { productIds, utmCampaign, utmMedium, utmSource } = constants.utmParameters.maamCpcMarket

    if (
      query.utm_source === utmSource
      && query.utm_medium === utmMedium
      && query.utm_campaign === utmCampaign
      && productIds.includes(id.value)
    ) {
      const delayBeforeShowingInSeconds = 10

      setTimeout(openCreateSubscriberModalWindow, 1000 * delayBeforeShowingInSeconds)
    }

    await trackPageView()
  }
})

onUnmounted(() => {
  aiReviewsStore.$reset()
})

function getPreviewUrl (): string | null {
  const imageUrls = helpers.getImageUrls(productData.value!.relationships.images.data)

  return imageUrls ? helpers.getImageUrl(imageUrls, previewTypes) : null
}

async function openAddProductToListModalWindow (): Promise<void> {
  await useModal({ component: AddProductToList, attrs: { productId: productData.value!.id } }).open()
}

async function openCreateSubscriberModalWindow (): Promise<void> {
  await useModal({ component: CreateSubscriber, attrs: { subscriptionForm: SubscriptionForm.Guide } }).open()
}

async function openShareLinkModalWindow (): Promise<void> {
  const link = `${constants.siteUrl}${helpers.getProductPath(productData.value!)}`

  await useModal({ component: ShareLink, attrs: { link } }).open()
}

function setTotalPublishedReviews (x: number): void {
  productData.value!.attributes.totalPublishedReviews = x
}

function setUserReviewId (x: string | null): void {
  productData.value!.meta.userReviewId = x
}

async function trackPageView (): Promise<void> {
  EcommerceApiService.viewItem(
    productData.value!.id,
    productData.value!.attributes.name,
    price.value,
    brandData.value ? brandData.value.attributes.name : null,
    categoriesData.value.map((x: CategoryData): string => x.attributes.name).sort()
  )

  viewProduct(productData.value!.id)
}
</script>

<template>
  <div>
    <Preloader v-if="status === 'pending'" />
    <template v-else-if="status === 'success' && productData">
      <section class="mb-[10px] md:mb-5">
        <div class="container">
          <div class="flex items-center justify-between">
            <NewTaxonomy :items="getBreadcrumbItems" />

            <a
              class="block !no-underline lg:hidden"
              href="#"
              @click.prevent="router.back"
            >
              <SvgIcon class="!size-6" name="outlined/arrow-back" />
            </a>

            <div class="flex items-center">
              <ProductLink
                v-if="authStore.isUser"
                class="mr-3 md:mr-4"
                icon="outlined/new-playlist-add"
                title="Добавить в список"
                @click.prevent="openAddProductToListModalWindow"
              />

              <ProductLink
                icon="outlined/new-share"
                title="Поделиться"
                @click.prevent="openShareLinkModalWindow"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="mb-3 md:mb-4">
        <div class="container">
          <div class="flex flex-col lg:flex-row">
            <ProductHeader
              class="mb-3 mr-0 w-full lg:mb-0 lg:mr-3 lg:w-[calc(100%-385px-12px)] xl:mr-6 xl:w-[calc(100%-443px-24px)]"
              :product-data="productData"
            />

            <ProductAside :product-data="productData" />
          </div>
        </div>
      </section>

      <section class="mb-3 md:mb-4">
        <div class="container">
          <DefaultBox class="flex flex-col md:flex-row md:items-stretch">
            <ProductBrand
              class="mb-6 border-b border-[#EEEEEE] pb-6 md:mb-0 md:mr-6 md:border-b-0 md:border-r md:pb-0 md:pr-6"
              :product-data="productData"
            />

            <ProductCategories :product-data="productData" />
          </DefaultBox>
        </div>
      </section>

      <section class="mb-10 md:mb-[60px]">
        <div class="container">
          <DefaultBox>
            <DefaultBoxSection>
              <ProductDescription :product-data="productData" />
            </DefaultBoxSection>

            <DefaultBoxSection
              :class="{
                '!mb-0 !border-none !pb-0': (
                  !productData?.relationships.researches?.data.length
                  && !productData?.relationships.facts?.data.length
                )
              }"
            >
              <ProductAttributes :product-data="productData" />
            </DefaultBoxSection>

            <DefaultBoxSection
              v-if="productData?.relationships.researches?.data.length"
              :class="{ '!mb-0 !border-none !pb-0': !productData?.relationships.facts?.data.length }"
            >
              <ProductResearches :product-data="productData" />
            </DefaultBoxSection>

            <DefaultBoxSection v-if="productData?.relationships.facts?.data.length" class="!mb-0 !border-none !pb-0">
              <ProductFacts :product-data="productData" />
            </DefaultBoxSection>
          </DefaultBox>
        </div>
      </section>

      <MindboxRecommendationsSection
        v-if="!isMarathon"
        class="mb-10"
        :data="{ recommendation: { limit: 12, product: { ids: { itab: id } } } }"
        :operation="
          productData.meta?.isOutOfStock
            ? MindboxOperation.SimilarProductsAmongOwnBrads
            : MindboxOperation.SimilarProducts
        "
        title="Похожие товары"
      />

      <MindboxRecommendationsSection
        v-if="!isMarathon"
        class="mb-10"
        :data="{ recommendation: { limit: 12, product: { ids: { itab: id } } } }"
        :operation="MindboxOperation.RelatedProducts"
        :title="'С\xA0этим товаром покупают'"
      />

      <section id="anchor4" class="mb-10 md:mb-[60px]">
        <div class="container">
          <DefaultBox>
            <SidebarWithMainSection>
              <template #sidebar>
                <ReviewsSidebar
                  v-if="activeTab === Tabs.Reviews"
                  :is-product-purchased-by-user="isPurchasedByUser"
                  :product-id="id"
                  :rating="rating"
                  :total-published-reviews="totalPublishedReviews"
                  :user-review-id="userReviewId"
                  @set-user-review-id="setUserReviewId"
                />
                <ProductQuestionsSectionSidebar v-else :product-id="id" />
              </template>

              <template #main>
                <ToggleRow class="mb-6 md:mb-[30px]">
                  <ToggleRowItem
                    :count="totalPublishedReviews"
                    :is-active="activeTab === Tabs.Reviews"
                    name="Отзывы"
                    @click.prevent="activeTab = Tabs.Reviews"
                  />

                  <ToggleRowItem
                    :count="productQuestionsStore.totalProductQuestions"
                    :is-active="activeTab === Tabs.Questions"
                    name="Вопросы"
                    @click.prevent="activeTab = Tabs.Questions"
                  />
                </ToggleRow>

                <ProductReviews
                  v-if="activeTab === Tabs.Reviews"
                  :is-product-purchased-by-user="isPurchasedByUser"
                  :product-id="id"
                  :user-review-id="userReviewId"
                  @set-total-published-reviews="setTotalPublishedReviews"
                />
                <ProductQuestionsSection v-else :product-id="productId" />
              </template>
            </SidebarWithMainSection>
          </DefaultBox>
        </div>
      </section>

      <ConsultationsSection v-if="!isConsultation && !isMarathon" class="mb-10" />

      <section>
        <div class="container">
          <Disclaimer class="content-wrapper" />
        </div>
      </section>

      <ProductFooterSticky v-if="productId" :multiplicity="multiplicity" :product-id="productId" />
    </template>
  </div>
</template>
